<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div data-test="usecase-select">
                    <vx-label size="md">Use case</vx-label>
                    <vx-selectable
                        v-model="form.usecase"
                        size="lg"
                        :options="props.enums?.usecases ?? []"
                        :loading="null === props.enums"
                        :clearable="false"
                        :error="displayFirstError(validate.usecase.$errors)"
                    ></vx-selectable>
                    <p class="text-sm text-slate-500 mt-1">
                        Almost always Marketing. Mixed needs separate transactional and marketing opt-in checkboxes on
                        the customer's form
                    </p>
                </div>

                <div
                    v-if="props.enums?.usecases_with_sub_usecases.includes(form.usecase)"
                    data-test="sub-usecases-select"
                >
                    <vx-label size="md">Sub use cases</vx-label>
                    <vx-selectable
                        v-model="form.sub_usecases"
                        multiple
                        size="lg"
                        :options="subUsecaseOptions"
                        :closeOnSelect="false"
                        :error="displayFirstError(validate.sub_usecases.$errors)"
                    ></vx-selectable>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Subscriber opt-in</vx-label>
                        <div class="text-sm text-slate-500">Fixed after creation</div>
                    </div>
                    <vx-toggle
                        v-model="form.subscriber_optin"
                        size="lg"
                        data-test="subscriber-optin-toggle"
                    ></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Subscriber opt-out</vx-label>
                        <div class="text-sm text-slate-500">Fixed after creation</div>
                    </div>
                    <vx-toggle
                        v-model="form.subscriber_optout"
                        size="lg"
                        data-test="subscriber-optout-toggle"
                    ></vx-toggle>
                </div>

                <div class="flex items-center justify-between">
                    <div>
                        <vx-label size="md">Subscriber help</vx-label>
                        <div class="text-sm text-slate-500">Fixed after creation</div>
                    </div>
                    <vx-toggle v-model="form.subscriber_help" size="lg" data-test="subscriber-help-toggle"></vx-toggle>
                </div>

                <div class="flex items-center gap-3">
                    <vx-checkbox
                        v-model="form.terms_and_conditions"
                        data-test="terms-checkbox"
                        :error="displayFirstError(validate.terms_and_conditions.$errors)"
                    ></vx-checkbox>
                    <div>
                        <vx-label size="md">Terms accepted</vx-label>
                        <div class="text-sm text-slate-500">TCR's terms and conditions for campaign registration</div>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxCheckbox, VxExpandableCard, VxLabel, VxSelectable, VxToggle } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, required, sameAs } from '@vuelidate/validators';
import { computed, toRef } from 'vue';
import type { TcrCampaignCreate, TcrEnums } from '~/services/adminTenDlcRegistrationClient.types';
import { displayFirstError } from '~/utils/validation';

const props = defineProps<{ enums: TcrEnums | null; externalResults: Record<string, string[]> }>();
const form = defineModel<TcrCampaignCreate>({ required: true });

const subUsecaseOptions = computed(() =>
    (props.enums?.usecases ?? []).filter((usecase) => !props.enums?.usecases_with_sub_usecases.includes(usecase))
);

const validate = useVuelidate(
    {
        usecase: { required },
        sub_usecases: {},
        terms_and_conditions: {
            accepted: helpers.withMessage('Accept the terms to register the campaign', sameAs(true)),
        },
    },
    form,
    { $externalResults: toRef(props, 'externalResults') }
);
</script>
